/** Dialoge der Lagerübersicht: Korrektur, Bewegungshistorie, Umbenennen, Eigenentnahme, Promotion.
 *  Alle Zustände bleiben in der Seite, hier steht nur die Darstellung. */
import { eur, dateFmt } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Th, Td, NumberInput } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dimStr } from "./InventoryTable";

export const AdjustDialog = ({ adj, onClose, af, setAf, onSave }) => {
  const { t } = useI18n();
  return (
    <Dialog open={!!adj} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("inv.correctionTitle")} · {adj?.product_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1"><span className="font-mono">{adj?.sku}</span>
          {adj && !adj.is_digital && dimStr(adj) !== "—" ? ` · ${dimStr(adj)}` : ""}</p>
        <p className="text-sm text-muted-foreground">{t("inv.correctionHint")}</p>
        <label className="flex items-center gap-2 rounded-md bg-muted/40 p-2 cursor-pointer text-sm" data-testid="adjust-reclass-label">
          <input type="checkbox" checked={af.reclass} data-testid="adjust-reclass"
            onChange={(e) => setAf({ ...af, reclass: e.target.checked, quality: e.target.checked ? "second_choice" : "good" })} />
          {t("inv.reclass")}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{af.reclass ? t("inv.reclassQty") : t("inv.qtySigned")}</Label>
            <NumberInput testId="adjust-qty" value={af.quantity_signed} className="mt-1.5"
              onChange={(val) => setAf({ ...af, quantity_signed: val })} /></div>
          <div><Label>{af.reclass ? t("inv.targetQuality") : t("inv.quality")}</Label>
            <Select value={af.quality} onValueChange={(v) => setAf({ ...af, quality: v })}>
              <SelectTrigger className="mt-1.5" data-testid="adjust-quality"><SelectValue /></SelectTrigger>
              <SelectContent>
                {!af.reclass && <SelectItem value="good">{t("quality.good")}</SelectItem>}
                <SelectItem value="defect">{t("quality.defect")}</SelectItem>
                <SelectItem value="second_choice">{t("quality.second_choice")}</SelectItem>
                <SelectItem value="blocked">{t("quality.blocked")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>{t("common.reason")}</Label>
          <Textarea data-testid="adjust-reason" value={af.reason} className="mt-1.5"
            onChange={(e) => setAf({ ...af, reason: e.target.value })} /></div>
        <DialogFooter>
          <Button data-testid="save-adjust-btn" onClick={onSave} disabled={!af.quantity_signed}>{t("common.book")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const HistoryDialog = ({ hist, onClose, qLabelOf }) => {
  const { t } = useI18n();
  return (
    <Dialog open={!!hist} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{t("inv.movements")} · {hist?.sku}</DialogTitle></DialogHeader>
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full">
            <thead><tr><Th>{t("inv.timestamp")}</Th><Th>{t("inv.type")}</Th><Th>{t("inv.quality")}</Th>
              <Th right>{t("common.quantity")}</Th><Th>{t("ord.colOrder")}</Th><Th>{t("common.reason")}</Th><Th>{t("inv.by")}</Th></tr></thead>
            <tbody>
              {hist?.moves?.map((m) => (
                <tr key={m.id}>
                  <Td>{dateFmt(m.occurred_at)}</Td>
                  <Td>{t(`movement.${m.movement_type}`, m.movement_type)}</Td>
                  <Td>{qLabelOf(m.quality)}</Td>
                  <Td right mono className={m.quantity_signed < 0 ? "text-destructive" : "text-primary"}>
                    {m.quantity_signed > 0 ? "+" : ""}{m.quantity_signed}</Td>
                  <Td mono className="text-xs" data-testid={`hist-order-${m.id}`}>
                    {m.order_id && m.order_number ? (
                      <a href={`/auftraege/${m.order_id}`} target="_blank" rel="noopener noreferrer"
                        className="text-primary hover:underline" data-testid={`hist-order-link-${m.id}`}>
                        {m.order_number}
                      </a>
                    ) : (m.order_number || "—")}
                  </Td>
                  <Td className="text-xs" data-testid={`hist-note-${m.id}`}>{m.note || m.reason || "—"}</Td>
                  <Td className="text-xs">{m.created_by_email || "—"}</Td>
                </tr>
              ))}
              {!hist?.moves?.length && <tr><Td className="text-muted-foreground">{t("inv.noMovements")}</Td></tr>}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const RenameDialog = ({ row, onClose, form, setForm, canRename, canEdit, busy, onSave }) => {
  const { t } = useI18n();
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("inv.editRowTitle")} · {row?.sku}</DialogTitle></DialogHeader>
        <DialogDescription>{row?.product_name}</DialogDescription>
        {canRename && (
          <div><Label>{t("inv.productName")}</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5" data-testid="rename-input" /></div>
        )}
        {canEdit && (<>
          <p className="text-sm text-muted-foreground">{t("inv.editRowHint")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("inv.newStock")}</Label>
              <NumberInput testId="edit-stock" value={form.physical} className="mt-1.5"
                onChange={(val) => setForm({ ...form, physical: val })} />
              <p className="mt-1 text-xs text-muted-foreground">{t("inv.physical")}: {row?.physical}</p></div>
            <div><Label>{t("inv.newSecond")}</Label>
              <NumberInput testId="edit-second" value={form.second} className="mt-1.5"
                onChange={(val) => setForm({ ...form, second: val })} />
              <p className="mt-1 text-xs text-muted-foreground">{t("quality.second_choice")}: {row?.second_choice}</p></div>
          </div>
          <div><Label>{t("common.reason")} ({t("common.optional")})</Label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="mt-1.5" data-testid="edit-reason" /></div>
        </>)}
        <DialogFooter>
          <Button onClick={onSave} disabled={busy} data-testid="save-rename-btn">
            {busy ? t("common.saving", t("common.save")) : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const OwnWithdrawalDialog = ({ own, onClose, qty, setQty, note, setNote, onSave, email }) => {
  const { t } = useI18n();
  return (
    <Dialog open={!!own} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("inv.ownWithdrawal")} · {own?.product_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1"><span className="font-mono">{own?.sku}</span> · {t("inv.available")}: {own?.physical}</p>
        <p className="text-sm text-muted-foreground">{t("inv.ownWithdrawalHint")}{" "}
          <strong>{eur(own?.cost_price)} {t("inv.costPerPiece")}</strong> · <strong>{email}</strong></p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{t("common.quantity")}</Label>
            <NumberInput testId="ownw-qty" value={qty} onChange={setQty} className="mt-1.5" /></div>
          <div className="flex items-end"><div className="text-sm text-muted-foreground">{t("inv.costTotal")}:{" "}
            <span className="font-mono font-semibold text-foreground">{eur((Number(qty) || 0) * (own?.cost_price || 0))}</span></div></div>
        </div>
        <div><Label>{t("common.note")} ({t("common.optional")})</Label>
          <Textarea data-testid="ownw-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" /></div>
        <DialogFooter>
          <Button data-testid="ownw-save-btn" onClick={onSave} disabled={!qty}>{t("inv.bookWithdrawal")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PromotionDialog = ({ promo, onClose, qty, setQty, event, setEvent, note, setNote, onSave }) => {
  const { t } = useI18n();
  return (
    <Dialog open={!!promo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("inv.promotion")} · {promo?.product_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1"><span className="font-mono">{promo?.sku}</span> · {t("inv.available")}: {promo?.physical}</p>
        <p className="text-sm text-muted-foreground">{t("inv.promotionHint")}{" "}
          <strong>{eur(promo?.cost_price)} {t("inv.costPerPiece")}</strong></p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{t("common.quantity")}</Label>
            <NumberInput testId="promo-qty" value={qty} onChange={setQty} className="mt-1.5" /></div>
          <div className="flex items-end"><div className="text-sm text-muted-foreground">{t("inv.costTotal")}:{" "}
            <span className="font-mono font-semibold text-foreground">{eur((Number(qty) || 0) * (promo?.cost_price || 0))}</span></div></div>
        </div>
        <div><Label>{t("inv.event")} ({t("common.optional")})</Label>
          <Input data-testid="promo-event" value={event} onChange={(e) => setEvent(e.target.value)} className="mt-1.5" /></div>
        <div><Label>{t("common.note")} ({t("common.optional")})</Label>
          <Textarea data-testid="promo-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" /></div>
        <DialogFooter>
          <Button data-testid="promo-save-btn" onClick={onSave} disabled={!qty}>{t("inv.promotion")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
